import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import { format, parse, isBefore } from 'date-fns';
import { BusinessHourModel } from '../../../models/OperationHoursModel';
import { Box, FormField, FormInputGroup, FormError } from '@vp/swan';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { formatStr, FormHourModel, isOverlapping, makeTimePickerField } from './HoursUtils';
import cloneDeep from 'lodash/cloneDeep';
import { defineMessages } from '@vistaprint-org/digital-i18n-utils';
import { useTranslation } from 'react-i18next';
import TrashIcon from '../../../images/trash.svg';

import './BusinessHour.scss';

interface Props {
  interval: BusinessHourModel;
  removeHours: () => void;
  operationHours: Array<BusinessHourModel>;
  hoursIndex: number;
  updateHourValue: (key: keyof BusinessHourModel, newValue: string) => void;
  setValidHour: (isValid: boolean) => void;
}

const today = new Date();

const messages = defineMessages({
  invalidInterval: 'Opening time cannot be after closing time',
  overlappingHours: 'Business hours are overlapping',
  openingHour: 'Opening Hour TimePicker',
  closingHour: 'Closing Hour TimePicker',
});

const BusinessHour: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { interval, removeHours, operationHours, hoursIndex, updateHourValue } = props;
  const initialData = {
    openingHour: interval.openingHour
      ? parse(interval.openingHour, formatStr, today)
      : new Date(today.getFullYear(), today.getMonth(), today.getDate(), 9, 0, 0),
    closingHour: interval.closingHour
      ? parse(interval.closingHour, formatStr, today)
      : new Date(today.getFullYear(), today.getMonth(), today.getDate(), 18, 0, 0),
  };

  // Validate initial data
  const initialIsValidInterval = isBefore(initialData.openingHour, initialData.closingHour);
  const initialInvalidIntervalError = initialIsValidInterval ? '' : t(messages.invalidInterval.id);

  const [state, setSate] = React.useState<FormHourModel>({
    data: initialData,
    errors: {
      invalidInterval: initialInvalidIntervalError,
      overlappingHours: '',
    },
  });
  const timePickerClasses = classNames('business-hour-time-picker', {
    'is-invalid': !isEmpty(state.errors.invalidInterval) || !isEmpty(state.errors.overlappingHours),
  });

  // Memoize operationHours to track actual changes
  const operationHoursKey = useMemo(() => JSON.stringify(operationHours), [operationHours]);

  // Check for overlapping hours when operationHours changes
  useEffect(() => {
    const overlappingHoursError = isOverlapping(operationHours, hoursIndex)
      ? t(messages.overlappingHours.id)
      : '';

    setSate((prevState) => {
      // Preserve the invalidInterval error, only update overlappingHours
      const newState = {
        ...prevState,
        errors: {
          ...prevState.errors,
          overlappingHours: overlappingHoursError,
        },
      };
      return newState;
    });
  }, [operationHoursKey, hoursIndex, t, operationHours]);

  // Notify parent whenever validation state changes
  const prevValidityRef = useRef<boolean | null>(null);
  useEffect(() => {
    const isValid = isEmpty(state.errors.overlappingHours) && isEmpty(state.errors.invalidInterval);
    // Only notify if validity has actually changed
    if (prevValidityRef.current !== isValid) {
      prevValidityRef.current = isValid;
      props.setValidHour(isValid);
    }
  }, [state.errors.overlappingHours, state.errors.invalidInterval]);

  function validateHour(formState: FormHourModel): FormHourModel {
    const newFormErrors = { ...formState.errors };
    const openingDate = formState.data.openingHour as Date;
    const closingDate = formState.data.closingHour as Date;
    const isValidInterval = isBefore(openingDate, closingDate);

    newFormErrors.invalidInterval = isValidInterval ? '' : t(messages.invalidInterval.id);

    // Note: overlap checking is handled by useEffect when operationHours updates
    // Don't check here because operationHours may contain stale data

    return { data: formState.data, errors: newFormErrors };
  }

  function updateHour(value: Date, key: keyof BusinessHourModel) {
    const formattedTime = value ? format(value, formatStr) : '';
    updateHourValue(key, formattedTime);

    // Update state with new value and run validation
    setSate((prevState) => {
      const newState = {
        ...prevState,
        data: {
          ...prevState.data,
          [key]: value,
        },
      };
      return validateHour(newState);
    });
    // Note: Parent will be notified by useEffect when state updates
  }

  return (
    <FormField>
      <FormInputGroup>
        <Box className="business-hour-row">
          {makeTimePickerField(
            state,
            'openingHour',
            updateHour,
            timePickerClasses,
            t(messages.openingHour.id)
          )}
          {makeTimePickerField(
            state,
            'closingHour',
            updateHour,
            timePickerClasses,
            t(messages.closingHour.id)
          )}
          <div className="business-hour-delete-container">
            <TrashIcon className="business-hour-delete" onClick={removeHours} />
          </div>
        </Box>
        {state.errors.invalidInterval && <FormError>{state.errors.invalidInterval}</FormError>}
        {state.errors.overlappingHours && <FormError>{state.errors.overlappingHours}</FormError>}
      </FormInputGroup>
    </FormField>
  );
};
export default BusinessHour;
